import { memo, useCallback } from 'react'

import type { UserFills } from '@/lib/utils/hyper/types'
import { useTranslations } from 'next-intl'
import { HStack, VStack, Text, Box } from '@chakra-ui/react'
import { HistoryCard } from './HistoryCard'

interface PositionHistoryItemProps {
  fill: UserFills[0]
  index: number
  totalItems: number
}

export const PositionHistoryItem = memo(function PositionHistoryItem({
  fill,
  index,
  totalItems,
}: PositionHistoryItemProps) {
  const t = useTranslations()

  // Calculate trade details
  const coin = fill.coin.replace('-PERP', '')
  const isLong = fill.side !== 'sell'
  const entryPrice = Number.parseFloat(fill.px)
  const size = Number.parseFloat(fill.sz)
  const sizeValue = entryPrice * size

  // Format currency values using the standard formatters

  // Helper function to format time relative to now
  const getTimeLabel = useCallback((timestamp: number) => {
    const now = Date.now() / 1000
    const diff = now - timestamp

    if (diff < 60) {
      return { type: 'relative', label: `${Math.floor(diff)}S Ago` }
    }
    else if (diff < 3600) {
      return { type: 'relative', label: `${Math.floor(diff / 60)}M Ago` }
    }
    else if (diff < 86400) {
      return { type: 'relative', label: `${Math.floor(diff / 3600)}H Ago` }
    }
    const date = new Date(timestamp * 1000)
    const year = date.getFullYear().toString().slice(2)
    const month = date.getMonth() + 1
    const day = date.getDate()
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return {
      type: 'absolute',
      date: `${year}.${month}.${day}`,
      time: `${hours}:${minutes}`,
    }
  }, [])

  const timeData = getTimeLabel(fill.time / 1000)

  return (
    <HStack w="full" align="start" gap={4}>
      {/* Timeline on the left */}
      <VStack w="50px" align="center" gap={0}>
        {/* Time badge */}
        <VStack 
          w="full" 
          h="50px" 
          align="center" 
          justify="center"
          rounded="full"
          bg="text.t1/10"
          px={2}
        >
          {timeData.type === 'relative' ? (
            <Text 
              textAlign="center" 
              fontSize="0.6875rem"
              color="text.t1"
              fontWeight="medium"
            >{timeData.label}</Text>
          ) : (
            <>
              <Text 
                textAlign="center" 
                fontSize="0.625rem"
                color="text.t1"
                fontWeight="medium"
              >{timeData.date}</Text>
              <Text
                textAlign="center" 
                fontSize="0.625rem"
                color="text.t1/60"
                fontWeight="medium"
              >{timeData.time}</Text>
            </>
          )}
        </VStack>

        {/* Connector line (except for last item) */}
        {index < totalItems - 1 && (
          <HStack w={3} justify="center">
            <Box h={6} borderLeft="1px dashed rgba(255, 255, 255, 0.3)"></Box>
          </HStack>
        )}
      </VStack>

      {/* History card on the right */}
      <HistoryCard fill={fill} />
    </HStack>
  )
})
